import {
  ReactNode,
  FC,
  useEffect,
  useRef,
  useState,
  ReactElement,
} from "react";
import {
  StyledCollapsible,
  CollapsibleTitle,
  CollapsibleContent,
  CollapsibleContentContainer,
  IconClosed,
  IconOpen,
  Wrapper,
} from "./Collapsible.ui";

export enum CollapsibleTestIds {
  Component = "collapsible",
  Title = "collapsible_title",
}

type CollapsibleProps = {
  id?: string;
  className?: string;
  children?: ReactNode;
  disabled?: boolean;
  isOpen?: boolean;
  onClick?: () => void;
  testId?: string;
  title: string | ReactElement;
  titleId?: string;
  accessory?: ReactNode;
};

export const Collapsible: FC<CollapsibleProps> = ({
  id,
  className,
  children,
  disabled,
  isOpen: isOpenProp = false,
  onClick,
  testId,
  title,
  titleId,
  accessory,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number>(0);
  const [isOpen, setIsOpen] = useState(isOpenProp);

  useEffect(() => {
    setIsOpen(isOpenProp);
  }, [isOpenProp]);

  useEffect(() => {
    if (isOpen) setHeight(ref.current?.getBoundingClientRect().height ?? 0);
    else setHeight(0);
  }, [isOpen]);

  useEffect(() => {
    if (!height || !isOpen || !ref.current) return undefined;
    const resizeObserver = new ResizeObserver((elem) => {
      const boxHeight = elem[0].borderBoxSize[0].blockSize;
      setHeight(boxHeight);
    });
    resizeObserver.observe(ref.current);
    return () => {
      resizeObserver.disconnect();
    };
  }, [height, isOpen]);

  const handleClick = () => {
    setIsOpen((prevState) => !prevState);
    onClick?.();
  };

  return (
    <StyledCollapsible
      id={id}
      className={className}
      data-testid={testId ?? CollapsibleTestIds.Component}
    >
      <CollapsibleTitle
        id={titleId}
        {...(!disabled && { onClick: handleClick })}
        $disabled={disabled}
        $isOpen={isOpen}
        data-testid={testId ? `${testId}_title` : CollapsibleTestIds.Title}
      >
        <Wrapper>
          {title}
          {accessory && accessory}
        </Wrapper>
        {isOpen ? (
          <IconOpen $disabled={disabled} />
        ) : (
          <IconClosed $disabled={disabled} />
        )}
      </CollapsibleTitle>
      <CollapsibleContent $height={height}>
        <CollapsibleContentContainer ref={ref}>
          {children}
        </CollapsibleContentContainer>
      </CollapsibleContent>
    </StyledCollapsible>
  );
};
